// User service for fetching user data from API
import Cookies from 'js-cookie';

export interface UserApiResponse {
    status: string;
    message: string;
    data: {
        data: {
            uuid: string;
            userName: string;
            userEmail: string;
            phone: string;
            team_uuid: string | null;
            user_active: boolean;
            email_notification: boolean;
            sms_notification: boolean;
            whatsapp_notifications: boolean;
            user_pause: boolean;
            created_at: string;
            updated_at: string;
            accounts_count?: number;
            role: {
                uuid: string;
                role_name: string;
                role_description: string;
            };
            team: {
                uuid: string;
                team_name: string;
            };
            client?: {
                client_name: string;
                is_client?: number; // 1 = client requiring mandatory status change on lead updates
            };
        };
    };
}

// Module-level deduplication: all callers (RouteGuard, UserContext, etc.) share one in-flight request
let _inFlight: Promise<UserApiResponse | null> | null = null;
let _cache: { data: UserApiResponse | null; ts: number } | null = null;
const CACHE_TTL = 5000; // 5 seconds

export async function fetchCurrentUser(): Promise<UserApiResponse | null> {
    // Return cached result if still fresh
    if (_cache && Date.now() - _cache.ts < CACHE_TTL) {
        return _cache.data;
    }

    // Deduplicate concurrent calls — return the same in-flight promise
    if (_inFlight) {
        return _inFlight;
    }

    _inFlight = (async () => {
        try {
            const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';
            const token = Cookies.get('crm_auth_token');

            if (!token) {
                console.error('No authentication token found in cookies');
                _cache = { data: null, ts: Date.now() };
                return null;
            }

            const response = await fetch(`${apiUrl}/users/me`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                console.error('Failed to fetch user data:', response.status);
                _cache = { data: null, ts: Date.now() };
                return null;
            }

            const data = await response.json();
            _cache = { data, ts: Date.now() };
            return data;
        } catch (error) {
            console.error('Error fetching user data:', error);
            _cache = { data: null, ts: Date.now() };
            return null;
        } finally {
            _inFlight = null;
        }
    })();

    return _inFlight;
}

// Call this on logout to clear the cache
export function clearUserCache(): void {
    _inFlight = null;
    _cache = null;
}
